/**
 * <bulk-order-paste-modal> owns the paste-specific logic: parsing +
 * validating the pasted text and dispatching `bulk-order:paste-import`.
 * SKU resolution itself is delegated to <bulk-order-workspace>, so that
 * logic (and its cache) lives in one place.
 *
 * The <dialog> mechanics (open/close, click-outside, Escape, scroll-lock,
 * animation) are delegated to the nested <dialog-component> -- the same
 * wrapper every other modal in this theme uses.
 */

import { parsePastedList } from './bulk-order-api.js';

const ERROR_AUTO_DISMISS_MS = 10000;

/** @typedef {import('./bulk-order-api.js').ParsedLine} ParsedLine */

/**
 * @typedef {{
 *   items:        ParsedLine[];
 *   skippedLines: string[];
 * }} PasteImportDetail
 *
 * @typedef {HTMLElement & { showDialog: () => void, closeDialog: () => Promise<void> }} DialogComponentElement
 */

class BulkOrderPasteModal extends HTMLElement {
  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;
  /** @type {number} pending "clear error" timeout, cancelled/replaced on every new error or manual clear */
  #errorDismissTimeout = 0;

  /** @type {DialogComponentElement} */
  dialogComponent;
  /** @type {HTMLDialogElement} */
  dialog;
  /** @type {HTMLTextAreaElement} */
  textarea;
  /** @type {HTMLElement} */
  errorEl;
  /** @type {HTMLButtonElement} */
  closeButton;
  /** @type {HTMLButtonElement} */
  cancelButton;
  /** @type {HTMLButtonElement} */
  importButton;

  connectedCallback() {
    this.dialogComponent = /** @type {DialogComponentElement} */ (this.querySelector('dialog-component'));
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('[data-paste-dialog]'));
    this.textarea = /** @type {HTMLTextAreaElement} */ (this.querySelector('[data-paste-textarea]'));
    this.errorEl = /** @type {HTMLElement} */ (this.querySelector('[data-paste-error]'));
    this.closeButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-paste-close]'));
    this.cancelButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-paste-cancel]'));
    this.importButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-paste-import]'));

    this.closeButton.addEventListener('click', this.close);
    this.cancelButton.addEventListener('click', this.close);
    this.importButton.addEventListener('click', this.#handleImport);
    this.dialog.addEventListener('close', this.#handleDialogClose);
    this.dialog.addEventListener('cancel', this.#handleDialogClose);
    this.textarea.addEventListener('input', this.#clearError);
  }

  disconnectedCallback() {
    this.closeButton.removeEventListener('click', this.close);
    this.cancelButton.removeEventListener('click', this.close);
    this.importButton.removeEventListener('click', this.#handleImport);
    this.dialog.removeEventListener('close', this.#handleDialogClose);
    this.dialog.removeEventListener('cancel', this.#handleDialogClose);
    this.textarea.removeEventListener('input', this.#clearError);
    clearTimeout(this.#errorDismissTimeout);
  }

  open = () => {
    this.#lastFocusedElement = /** @type {HTMLElement | null} */ (document.activeElement);
    this.textarea.value = '';
    this.#clearError();
    this.dialogComponent.showDialog();
    this.textarea.focus();
  };

  close = () => {
    this.dialogComponent.closeDialog();
  };

  #handleDialogClose = () => {
    // Return focus to whatever triggered the modal (the "Paste List"
    // button) rather than leaving it stranded on a now-hidden element.
    this.#lastFocusedElement?.focus?.();
  };

  #clearError = () => {
    clearTimeout(this.#errorDismissTimeout);
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  };

  #handleImport = () => {
    const rawText = this.textarea.value;
    if (!rawText.trim()) {
      this.#showError(this.dataset.emptyError || 'Paste at least one line before importing.');
      return;
    }

    const parsed = parsePastedList(rawText);
    const invalidLines = parsed.filter((line) => line.invalid);

    if (invalidLines.length === parsed.length) {
      this.#showError(this.dataset.allInvalidError || "Couldn't read any part numbers from that list.");
      return;
    }

    // Partial-failure case: import what parsed successfully, and let the
    // workspace surface which raw lines were skipped so nothing is lost
    // silently.
    document.dispatchEvent(
      new CustomEvent('bulk-order:paste-import', {
        bubbles: true,
        detail: {
          items: parsed.filter((line) => !line.invalid),
          skippedLines: invalidLines.map((line) => line.raw),
        },
      })
    );

    this.close();
  };

  /** @param {string} message */
  #showError(message) {
    clearTimeout(this.#errorDismissTimeout);

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    this.textarea.setAttribute('aria-invalid', 'true');
    this.textarea.focus();

    this.#errorDismissTimeout = window.setTimeout(() => {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.textarea.removeAttribute('aria-invalid');
    }, ERROR_AUTO_DISMISS_MS);
  }
}

customElements.define('bulk-order-paste-modal', BulkOrderPasteModal);
